import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const HostelData = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [hostels, setHostels] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState(null);
  const [hostelDetails, setHostelDetails] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [userId, setUserId] = useState(null);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get user ID
  useEffect(() => {
    const id = userInfo?.userId || sessionStorage.getItem('userId') || 1;
    setUserId(id);
  }, [userInfo]);

  // Fetch hostels
  useEffect(() => {
    if (userId) {
      fetchHostels();
    }
  }, [userId]);

  const fetchHostels = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/owner/hostels/${userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
        if (data.hostels && data.hostels.length > 0) {
          setSelectedHostel(data.hostels[0].hostel_id);
          // Pass the hostel data directly instead of trying to find it from state
          fetchHostelDetails(data.hostels[0].hostel_id, data.hostels[0]);
        }
      } else {
        setError(data.message || 'Failed to load hostels');
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostelDetails = async (hostelId, hostelData = null) => {
    try {
      setError(null);

      // Use provided hostel data or find from state
      const hostel = hostelData || hostels.find(h => h.hostel_id === hostelId);
      if (hostel) {
        setHostelDetails(hostel);

        // Generate mock room data based on hostel info
        const mockRooms = generateMockRooms(hostelId, hostel.total_rooms, hostel.occupied_rooms);
        const mockStudents = generateMockStudents(hostel.total_students);

        setRooms(mockRooms);
        setStudents(mockStudents);
      }
    } catch (err) {
      console.error('Error fetching hostel details:', err);
      setError('Failed to load hostel details');
    }
  };

  const generateMockRooms = (hostelId, totalRooms, occupiedRooms) => {
    const roomTypes = ['single', 'double', 'triple', 'dormitory'];
    const mockRooms = [];
    let occupiedCount = 0;

    for (let i = 1; i <= totalRooms; i++) {
      const isOccupied = occupiedCount < occupiedRooms;
      const roomType = roomTypes[Math.floor(Math.random() * roomTypes.length)];
      const floor = Math.ceil(i / 10);
      const bedsInRoom = roomType === 'single' ? 1 : roomType === 'double' ? 2 : roomType === 'triple' ? 3 : 6;

      mockRooms.push({
        room_id: i,
        room_number: `${floor}0${i % 10 || 10}`,
        floor_number: floor,
        block_name: String.fromCharCode(65 + Math.floor((i - 1) / 10)),
        room_type: roomType,
        is_ac: Math.random() > 0.6,
        rent_amount: roomType === 'single' ? 8000 : roomType === 'double' ? 6000 : roomType === 'triple' ? 5000 : 4000,
        bed_status: isOccupied ? 'occupied' : 'available',
        occupied_beds: isOccupied ? Math.ceil(Math.random() * bedsInRoom) : 0,
        total_beds: bedsInRoom,
        student_names: isOccupied ? [`Student ${i * 10}`, `Student ${i * 10 + 1}`].slice(0, Math.ceil(Math.random() * bedsInRoom)) : [],
        last_maintenance: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
      });

      if (isOccupied) occupiedCount++;
    }

    return mockRooms;
  };

  const generateMockStudents = (totalStudents) => {
    const students = [];
    const firstNames = ['Rajesh', 'Priya', 'Amit', 'Sneha', 'Vikram', 'Ananya', 'Arjun', 'Divya', 'Rohan', 'Zara'];
    const lastNames = ['Kumar', 'Singh', 'Patel', 'Sharma', 'Verma', 'Gupta', 'Yadav', 'Mishra', 'Iyer', 'Nair'];

    for (let i = 1; i <= totalStudents; i++) {
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      students.push({
        student_id: i,
        student_name: `${firstName} ${lastName}`,
        roll_number: `STU${String(i).padStart(5, '0')}`,
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@college.edu`,
        phone: `9${String(Math.floor(Math.random() * 1000000000)).padStart(9, '0')}`,
        room_number: `${Math.ceil(Math.random() * 10)}0${Math.floor(Math.random() * 10) + 1}`,
        assigned_date: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toLocaleDateString(),
        status: 'active'
      });
    }

    return students;
  };

  const handleNavigation = (page) => {
    if (isMobile) {
      setSidebarOpen(false);
    }
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const MenuItem = ({ label, icon, onClick }) => (
    <div className="menu-item" onClick={onClick}>
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
    </div>
  );

  const StatCard = ({ label, value, subtext, emoji, color }) => (
    <div className="stat-card">
      <div className="stat-emoji">{emoji}</div>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-subtext">{subtext}</div>
    </div>
  );

  const OverviewTab = () => {
    if (!hostelDetails) return null;

    const totalBeds = rooms.reduce((sum, room) => sum + room.total_beds, 0);
    const occupiedBeds = rooms.reduce((sum, room) => sum + room.occupied_beds, 0);

    return (
      <div className="tab-content">
        {/* Basic Info */}
        <div className="info-section">
          <h3 className="section-header">🏢 Hostel Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Hostel Name</span>
              <span className="info-value">{hostelDetails.hostel_name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Type</span>
              <span className="info-value info-badge">{hostelDetails.hostel_type}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Address</span>
              <span className="info-value">{hostelDetails.hostel_address || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className="info-value info-badge active">{hostelDetails.is_active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        </div>

        {/* Warden Info */}
        <div className="info-section">
          <h3 className="section-header">👤 Warden Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Warden Name</span>
              <span className="info-value">{hostelDetails.warden_name || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Contact Number</span>
              <span className="info-value">{hostelDetails.warden_phone || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Hostel Contact</span>
              <span className="info-value">{hostelDetails.hostel_contact || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="stats-section">
          <h3 className="section-header">📊 Statistics</h3>
          <div className="stats-grid">
            <StatCard
              emoji="🚪"
              label="Total Rooms"
              value={hostelDetails.total_rooms}
              subtext="All rooms"
              color="#0d9488"
            />
            <StatCard
              emoji="👥"
              label="Students"
              value={hostelDetails.total_students}
              subtext={`${((hostelDetails.total_students / (hostelDetails.total_rooms * 2)) * 100).toFixed(0)}% occupied`}
              color="#14b8a6"
            />
            <StatCard
              emoji="📈"
              label="Occupancy"
              value={`${hostelDetails.occupancy_percentage}%`}
              subtext={`${hostelDetails.occupied_rooms}/${hostelDetails.total_rooms} rooms`}
              color="#06b6d4"
            />
          </div>
        </div>

        {/* Room Status Chart */}
        <div className="chart-section">
          <h3 className="section-header">🛏️ Bed Status Overview</h3>
          <div className="room-status-chart">
            <div className="chart-item">
              <div className="chart-bar">
                <div className="chart-fill occupied" style={{ width: `${(occupiedBeds / totalBeds) * 100}%` }}></div>
              </div>
              <div className="chart-label">
                <span className="chart-color occupied"></span>
                <span className="chart-text">Occupied: {occupiedBeds} beds</span>
              </div>
            </div>
            <div className="chart-item">
              <div className="chart-bar">
                <div className="chart-fill available" style={{ width: `${((totalBeds - occupiedBeds) / totalBeds) * 100}%` }}></div>
              </div>
              <div className="chart-label">
                <span className="chart-color available"></span>
                <span className="chart-text">Available: {totalBeds - occupiedBeds} beds</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RoomsTab = () => {
    const occupiedRooms = rooms.filter(r => r.bed_status === 'occupied').length;
    const availableRooms = rooms.filter(r => r.bed_status === 'available').length;
    const acRooms = rooms.filter(r => r.is_ac).length;
    
    const roomTypes = {
      single: rooms.filter(r => r.room_type === 'single').length,
      double: rooms.filter(r => r.room_type === 'double').length,
      triple: rooms.filter(r => r.room_type === 'triple').length,
      dormitory: rooms.filter(r => r.room_type === 'dormitory').length
    };

    const totalBeds = rooms.reduce((sum, r) => sum + r.total_beds, 0);
    const occupiedBeds = rooms.reduce((sum, r) => sum + r.occupied_beds, 0);

    return (
      <div className="tab-content">
        {/* Room Summary Statistics */}
        <div className="summary-section">
          <h3 className="section-header">📊 Room Summary</h3>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-emoji">🚪</div>
              <div className="summary-label">Total Rooms</div>
              <div className="summary-value">{rooms.length}</div>
            </div>
            <div className="summary-card occupied">
              <div className="summary-emoji">✓</div>
              <div className="summary-label">Occupied</div>
              <div className="summary-value">{occupiedRooms}</div>
            </div>
            <div className="summary-card available">
              <div className="summary-emoji">📭</div>
              <div className="summary-label">Available</div>
              <div className="summary-value">{availableRooms}</div>
            </div>
            <div className="summary-card">
              <div className="summary-emoji">❄️</div>
              <div className="summary-label">AC Rooms</div>
              <div className="summary-value">{acRooms}</div>
            </div>
          </div>
        </div>

        {/* Bed Statistics */}
        <div className="stats-section">
          <h3 className="section-header">🛏️ Bed Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Beds</div>
              <div className="stat-value">{totalBeds}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Occupied Beds</div>
              <div className="stat-value occupied">{occupiedBeds}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Available Beds</div>
              <div className="stat-value available">{totalBeds - occupiedBeds}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Occupancy Rate</div>
              <div className="stat-value">{totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0}%</div>
            </div>
          </div>
        </div>

        {/* Room Type Distribution */}
        <div className="distribution-section">
          <h3 className="section-header">📈 Room Type Distribution</h3>
          <div className="distribution-grid">
            <div className="distribution-card">
              <div className="distribution-icon">1️⃣</div>
              <div className="distribution-label">Single Rooms</div>
              <div className="distribution-value">{roomTypes.single}</div>
            </div>
            <div className="distribution-card">
              <div className="distribution-icon">2️⃣</div>
              <div className="distribution-label">Double Rooms</div>
              <div className="distribution-value">{roomTypes.double}</div>
            </div>
            <div className="distribution-card">
              <div className="distribution-icon">3️⃣</div>
              <div className="distribution-label">Triple Rooms</div>
              <div className="distribution-value">{roomTypes.triple}</div>
            </div>
            <div className="distribution-card">
              <div className="distribution-icon">👥</div>
              <div className="distribution-label">Dormitory</div>
              <div className="distribution-value">{roomTypes.dormitory}</div>
            </div>
          </div>
        </div>

        {/* Visual Progress Bars */}
        <div className="progress-section">
          <h3 className="section-header">📊 Room Occupancy Status</h3>
          <div className="progress-items">
            <div className="progress-item">
              <div className="progress-label">
                <span>Rooms Occupied</span>
                <span className="progress-percent">{rooms.length > 0 ? Math.round((occupiedRooms / rooms.length) * 100) : 0}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill occupied" style={{ width: `${rooms.length > 0 ? (occupiedRooms / rooms.length) * 100 : 0}%` }}></div>
              </div>
              <div className="progress-detail">{occupiedRooms} of {rooms.length} rooms</div>
            </div>

            <div className="progress-item">
              <div className="progress-label">
                <span>Beds Occupied</span>
                <span className="progress-percent">{totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0}%</span>
              </div>
              <div className="progress-bar">
                <div className="progress-fill occupied" style={{ width: `${totalBeds > 0 ? (occupiedBeds / totalBeds) * 100 : 0}%` }}></div>
              </div>
              <div className="progress-detail">{occupiedBeds} of {totalBeds} beds</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StudentsTab = () => {
    return (
      <div className="tab-content">
        <div className="students-container">
          <div className="section-header">👥 All Students</div>
          <div className="table-wrapper">
            <table className="students-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Roll Number</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Room</th>
                  <th>Since</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => (
                  <tr key={student.student_id}>
                    <td className="student-name">{student.student_name}</td>
                    <td>{student.roll_number}</td>
                    <td className="email-cell">{student.email}</td>
                    <td>{student.phone}</td>
                    <td className="room-cell">{student.room_number}</td>
                    <td>{student.assigned_date}</td>
                    <td>
                      <span className="status-badge active">{student.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const StudentCard = ({ student }) => (
    <div className="student-card">
      <div className="student-card-header">
        <div className="student-avatar">{student.student_name.charAt(0)}</div>
        <div>
          <div className="student-card-name">{student.student_name}</div>
          <div className="student-card-roll">{student.roll_number}</div>
        </div>
      </div>
      <div className="student-card-details">
        <div className="card-detail">
          <span className="detail-label">Email</span>
          <span className="detail-value">{student.email}</span>
        </div>
        <div className="card-detail">
          <span className="detail-label">Phone</span>
          <span className="detail-value">{student.phone}</span>
        </div>
        <div className="card-detail">
          <span className="detail-label">Room</span>
          <span className="detail-value">{student.room_number}</span>
        </div>
        <div className="card-detail">
          <span className="detail-label">Since</span>
          <span className="detail-value">{student.assigned_date}</span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading hostel data...</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-container {
          display: flex;
          min-height: 100vh;
          background: linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          color: #1e293b;
        }

        /* Sidebar Styles */
        .sidebar {
          width: 280px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          border-right: 1px solid #e2e8f0;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          display: flex;
          flex-direction: column;
          transition: transform 0.3s ease;
          z-index: 1000;
          overflow-y: auto;
        }

        .sidebar.closed {
          transform: translateX(-280px);
        }

        .sidebar.opened {
          transform: translateX(0);
        }

        .sidebar-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 999;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        @media (max-width: 768px) {
          .sidebar-overlay.active {
            display: block;
            opacity: 1;
          }
        }

        .user-info-sidebar {
          padding: 30px 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          border-bottom: 1px solid #e2e8f0;
        }

        .user-avatar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 20px;
          flex-shrink: 0;
        }

        .user-name {
          font-weight: 600;
          color: #1e293b;
        }

        .user-role {
          font-size: 12px;
          color: #64748b;
        }

        .sidebar-nav {
          flex: 1;
          padding: 30px 0;
        }

        .nav-label {
          padding: 0 20px;
          font-size: 12px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 15px;
        }

        .menu-item {
          padding: 15px 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 15px;
          color: #64748b;
          transition: all 0.2s ease;
          border-left: 3px solid transparent;
        }

        .menu-item:hover {
          background: #f1f5f9;
          color: #0d9488;
          border-left-color: #0d9488;
        }

        .menu-icon {
          font-size: 18px;
        }

        .menu-label {
          font-weight: 500;
        }

        .logout-section {
          padding: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .logout-btn {
          width: 100%;
          padding: 12px;
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          color: #dc2626;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          background: #fecaca;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          margin-left: 280px;
          display: flex;
          flex-direction: column;
          transition: margin-left 0.3s ease;
        }

        .main-content.full {
          margin-left: 0;
        }

        .header {
          background: white;
          padding: 20px 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .menu-toggle {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #0d9488;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .menu-toggle:hover {
          background: #f1f5f9;
        }

        .header-title {
          font-size: 24px;
          font-weight: 700;
          color: #0d9488;
        }

        .content-area {
          flex: 1;
          padding: 30px;
          overflow-y: auto;
        }

        /* Hostel Selector */
        .hostel-selector {
          background: white;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 30px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .selector-label {
          font-weight: 600;
          margin-bottom: 12px;
          color: #1e293b;
        }

        .hostel-buttons {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        .hostel-btn {
          padding: 10px 20px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .hostel-btn.active {
          background: #0d9488;
          color: white;
          border-color: #0d9488;
        }

        .hostel-btn:hover {
          border-color: #0d9488;
        }

        /* Tab Navigation */
        .tab-navigation {
          display: flex;
          gap: 10px;
          background: white;
          padding: 20px;
          border-radius: 12px;
          margin-bottom: 20px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .tab-btn {
          padding: 10px 20px;
          border: none;
          background: #f1f5f9;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          color: #64748b;
          white-space: nowrap;
          transition: all 0.2s ease;
        }

        .tab-btn.active {
          background: #0d9488;
          color: white;
        }

        .tab-btn:hover {
          background: #e0f2fe;
        }

        .tab-btn.active:hover {
          background: #0d9488;
        }

        /* Tab Content */
        .tab-content {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        /* Info Section */
        .info-section {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .section-header {
          font-size: 16px;
          font-weight: 700;
          color: #0d9488;
          margin-bottom: 20px;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .info-label {
          font-weight: 600;
          color: #64748b;
          font-size: 12px;
          text-transform: uppercase;
        }

        .info-value {
          font-size: 15px;
          color: #1e293b;
          font-weight: 600;
        }

        .info-badge {
          display: inline-block;
          padding: 6px 12px;
          background: #e0f2fe;
          color: #0369a1;
          border-radius: 6px;
          font-size: 12px;
          width: fit-content;
        }

        .info-badge.active {
          background: #d1fae5;
          color: #065f46;
        }

        /* Statistics */
        .stats-section {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
        }

        .stat-card {
          background: linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%);
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          border: 1px solid #d1fae5;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .stat-emoji {
          font-size: 32px;
          margin-bottom: 10px;
        }

        .stat-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .stat-subtext {
          font-size: 12px;
          color: #64748b;
        }

        /* Chart Section */
        .chart-section {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .room-status-chart {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .chart-item {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .chart-bar {
          width: 100%;
          height: 30px;
          background: #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
        }

        .chart-fill {
          height: 100%;
          transition: width 0.3s ease;
        }

        .chart-fill.occupied {
          background: linear-gradient(90deg, #0d9488, #14b8a6);
        }

        .chart-fill.available {
          background: linear-gradient(90deg, #5eead4, #7dd3c0);
        }

        .chart-label {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .chart-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
        }

        .chart-color.occupied {
          background: #0d9488;
        }

        .chart-color.available {
          background: #5eead4;
        }

        .chart-text {
          font-size: 14px;
          color: #1e293b;
          font-weight: 600;
        }

        /* Rooms Container */
        .rooms-container {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .rooms-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .room-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .room-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .room-card.occupied {
          border-color: #0d9488;
          background: #f0fdfa;
        }

        .room-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 10px;
        }

        .room-number {
          font-size: 16px;
          font-weight: 700;
          color: #1e293b;
        }

        .room-status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
        }

        .room-status-badge.occupied {
          background: #d1fae5;
          color: #065f46;
        }

        .room-status-badge.available {
          background: #fee2e2;
          color: #dc2626;
        }

        .room-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
        }

        .detail-label {
          color: #64748b;
          font-weight: 600;
        }

        .detail-value {
          color: #1e293b;
          font-weight: 600;
        }

        .room-students {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 2px solid #e2e8f0;
        }

        .students-label {
          font-weight: 600;
          color: #64748b;
          font-size: 12px;
          margin-bottom: 8px;
        }

        .student-tag {
          display: inline-block;
          background: #0d9488;
          color: white;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 11px;
          margin-right: 6px;
          margin-bottom: 6px;
        }

        /* Room Summary Sections */
        .summary-section,
        .stats-section,
        .distribution-section,
        .progress-section {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
          margin-bottom: 20px;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .summary-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .summary-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .summary-card.occupied {
          background: #f0fdfa;
          border-color: #0d9488;
        }

        .summary-card.available {
          background: #fef2f2;
          border-color: #dc2626;
        }

        .summary-emoji {
          font-size: 32px;
          margin-bottom: 10px;
        }

        .summary-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .summary-value {
          font-size: 32px;
          font-weight: 700;
          color: #0d9488;
        }

        .summary-card.occupied .summary-value {
          color: #0d9488;
        }

        .summary-card.available .summary-value {
          color: #dc2626;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 15px;
          margin-top: 20px;
        }

        .stat-item {
          background: linear-gradient(135deg, #f0fdfa 0%, #e0f2fe 100%);
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          border: 1px solid #d1fae5;
        }

        .stat-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin-bottom: 8px;
        }

        .stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #0d9488;
        }

        .stat-value.occupied {
          color: #0d9488;
        }

        .stat-value.available {
          color: #06b6d4;
        }

        /* Distribution Grid */
        .distribution-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
          margin-top: 20px;
        }

        .distribution-card {
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          transition: all 0.3s ease;
        }

        .distribution-card:hover {
          border-color: #0d9488;
          background: #f0fdfa;
        }

        .distribution-icon {
          font-size: 28px;
          margin-bottom: 8px;
        }

        .distribution-label {
          font-size: 11px;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 8px;
        }

        .distribution-value {
          font-size: 24px;
          font-weight: 700;
          color: #0d9488;
        }

        /* Progress Section */
        .progress-items {
          display: flex;
          flex-direction: column;
          gap: 25px;
          margin-top: 20px;
        }

        .progress-item {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .progress-label {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
          color: #1e293b;
        }

        .progress-percent {
          background: #0d9488;
          color: white;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
        }

        .progress-bar {
          width: 100%;
          height: 12px;
          background: #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #0d9488, #14b8a6);
          transition: width 0.3s ease;
          border-radius: 6px;
        }

        .progress-fill.occupied {
          background: linear-gradient(90deg, #0d9488, #14b8a6);
        }

        .progress-detail {
          font-size: 12px;
          color: #64748b;
        }

        /* Students Container */
        .students-container {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        }

        .table-wrapper {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .students-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 20px;
        }

        .students-table thead {
          background: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .students-table th {
          padding: 15px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
          text-transform: uppercase;
          color: #64748b;
        }

        .students-table td {
          padding: 15px;
          border-bottom: 1px solid #e2e8f0;
          font-size: 13px;
        }

        .students-table tbody tr {
          transition: all 0.2s ease;
        }

        .students-table tbody tr:hover {
          background: #f8fafc;
        }

        .student-name {
          font-weight: 600;
          color: #1e293b;
        }

        .email-cell {
          color: #0d9488;
          text-decoration: none;
        }

        .room-cell {
          background: #f0fdfa;
          padding: 8px 12px;
          border-radius: 6px;
          display: inline-block;
          font-weight: 600;
          color: #0d9488;
        }

        .status-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
        }

        .status-badge.active {
          background: #d1fae5;
          color: #065f46;
        }

        /* Student Cards (Mobile) */
        .student-card {
          background: white;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border-left: 4px solid #0d9488;
          display: none;
        }

        .student-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 15px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 12px;
        }

        .student-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 18px;
        }

        .student-card-name {
          font-weight: 700;
          color: #1e293b;
          font-size: 14px;
        }

        .student-card-roll {
          font-size: 12px;
          color: #64748b;
        }

        .student-card-details {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .card-detail {
          display: flex;
          justify-content: space-between;
          font-size: 12px;
        }

        .card-detail .detail-label {
          color: #64748b;
        }

        .card-detail .detail-value {
          color: #1e293b;
          font-weight: 600;
        }

        /* Loading */
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          gap: 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #0d9488;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Error Message */
        .error-message {
          background: #fee2e2;
          border: 1px solid #fecaca;
          border-radius: 12px;
          padding: 20px;
          color: #dc2626;
          text-align: center;
          margin: 20px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-280px);
          }

          .sidebar.opened {
            transform: translateX(0);
            box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
          }

          .main-content {
            margin-left: 0 !important;
          }

          .header {
            padding: 12px 16px;
          }

          .header-title {
            font-size: 18px;
          }

          .content-area {
            padding: 20px 15px;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }

          .rooms-grid {
            grid-template-columns: 1fr;
          }

          .hostel-buttons {
            gap: 8px;
          }

          .hostel-btn {
            padding: 8px 16px;
            font-size: 13px;
          }

          .tab-navigation {
            gap: 8px;
            padding: 15px;
          }

          .tab-btn {
            padding: 8px 16px;
            font-size: 12px;
          }

          .info-section,
          .stats-section,
          .chart-section,
          .rooms-container,
          .students-container {
            padding: 16px;
          }

          .students-table {
            font-size: 11px;
          }

          .students-table th,
          .students-table td {
            padding: 10px;
          }

          .students-table {
            display: none;
          }

          .student-card {
            display: block;
          }

          .section-header {
            font-size: 15px;
          }

          .stat-card {
            padding: 15px;
          }

          .stat-value {
            font-size: 24px;
          }

          .stat-emoji {
            font-size: 28px;
          }
        }

        @media (max-width: 480px) {
          .header {
            padding: 10px 12px;
          }

          .header-title {
            font-size: 16px;
          }

          .content-area {
            padding: 15px 10px;
          }

          .info-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .rooms-grid {
            grid-template-columns: 1fr;
          }

          .room-card {
            padding: 15px;
          }

          .info-section,
          .stats-section,
          .chart-section,
          .rooms-container,
          .students-container {
            padding: 12px;
          }

          .section-header {
            font-size: 14px;
            margin-bottom: 15px;
          }

          .stat-card {
            padding: 12px;
          }

          .stat-value {
            font-size: 20px;
          }

          .stat-emoji {
            font-size: 24px;
          }

          .student-card {
            padding: 12px;
            margin-bottom: 10px;
          }

          .tab-navigation {
            gap: 6px;
            padding: 12px;
            border-radius: 8px;
          }

          .tab-btn {
            padding: 8px 12px;
            font-size: 11px;
          }

          .hostel-selector {
            padding: 15px;
          }

          .hostel-buttons {
            gap: 6px;
          }

          .hostel-btn {
            padding: 8px 12px;
            font-size: 12px;
            flex: 1;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar Overlay for Mobile */}
        {isMobile && (
          <div 
            className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`sidebar ${!sidebarOpen ? 'closed' : ''} ${isMobile && sidebarOpen ? 'opened' : ''}`}>
          <div className="user-info-sidebar">
            <div className="user-avatar">
              {userInfo?.fullName?.charAt(0)?.toUpperCase() || 'O'}
            </div>
            <div>
              <div className="user-name">{userInfo?.fullName || 'Owner'}</div>
              <div className="user-role">Property Owner</div>
            </div>
          </div>

          <div className="sidebar-nav">
            <div className="nav-label">Main Menu</div>
            <MenuItem
              label="Dashboard"
              icon="📊"
              onClick={() => handleNavigation('hostel-owner-dashboard')}
            />
            <MenuItem
              label="Inventory Management"
              icon="📦"
              onClick={() => handleNavigation('hostel-owner-inventory')}
            />
            <MenuItem
              label="See Hostel Data"
              icon="🏢"
              onClick={() => handleNavigation('hostel-owner-hosteldata')}
            />
          </div>

          <div className="logout-section">
            <button className="logout-btn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className={`main-content ${!sidebarOpen ? 'full' : ''}`}>
          <div className="header">
            <div className="header-left">
              <button
                className="menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              >
                {sidebarOpen ? '✕' : '☰'}
              </button>
              <div className="header-title">🏢 Hostel Data</div>
            </div>
          </div>

          <div className="content-area">
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Hostel Selector */}
            {hostels.length > 0 && (
              <div className="hostel-selector">
                <div className="selector-label">📍 Select Hostel</div>
                <div className="hostel-buttons">
                  {hostels.map(hostel => (
                    <button
                      key={hostel.hostel_id}
                      className={`hostel-btn ${selectedHostel === hostel.hostel_id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedHostel(hostel.hostel_id);
                        fetchHostelDetails(hostel.hostel_id, hostel);
                        setActiveTab('overview');
                      }}
                    >
                      {hostel.hostel_name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {hostelDetails && (
              <>
                {/* Tab Navigation */}
                <div className="tab-navigation">
                  <button
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                  >
                    📊 Overview
                  </button>
                  <button
                    className={`tab-btn ${activeTab === 'rooms' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rooms')}
                  >
                    🚪 Rooms ({rooms.length})
                  </button>
                  <button
                    className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
                    onClick={() => setActiveTab('students')}
                  >
                    👥 Students ({students.length})
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && <OverviewTab />}
                {activeTab === 'rooms' && <RoomsTab />}
                {activeTab === 'students' && <StudentsTab />}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HostelData;